import React, { useCallback, useEffect, useRef, useState } from 'react';


import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Grid,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Folder, Description } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import ImageIcon from '@mui/icons-material/Image';
import { API_BASE_URL } from '../../constant/constant';

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf': return <PictureAsPdfIcon sx={{ mr: 1, color: 'red' }} />;
    case 'txt': return <DescriptionIcon sx={{ mr: 1, color: 'blue' }} />;
    case 'doc':
    case 'docx': return <ArticleIcon sx={{ mr: 1, color: 'blue' }} />;
    case 'xls':
    case 'xlsx':
    case 'csv': return <TableChartIcon sx={{ mr: 1, color: 'green' }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif': return <ImageIcon sx={{ mr: 1, color: 'purple' }} />;
    default: return <InsertDriveFileIcon sx={{ mr: 1 }} />;
  }
};

const FolderViewer = () => {
  const [loading, setLoading] = useState(false);
  const [reportTree, setReportTree] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilePath, setSelectedFilePath] = useState(null);
  const [selectedFolderPath, setSelectedFolderPath] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [contextFile, setContextFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef();
  //const lastSelectedNodeRef = useRef(null);
  const lastSelectedPathRef = useRef('');
  const [contextFolderPath, setContextFolderPath] = useState(null);
  const lastSelectedNodeRef = useRef(null);
  const [fileContextMenu, setFileContextMenu] = useState(null);
  const token = localStorage.getItem("token");

  const reselectLastFolder = () => {
    if (lastSelectedPathRef.current && reportTree) {
      const node = findNodeByPath(reportTree, lastSelectedPathRef.current);
      if (node) {
        setSelectedFiles(node.files || []);
        setSelectedFilePath(null);
        setSelectedFolderPath(node.path);
      }
    }
  };



  const loadReportTree = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/reports`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // optional, set default headers
      },
    })
      .then(res => res.json())
      .then(data => setReportTree(data))
      .catch(err => console.error('Failed to load report tree:', err))
      .finally(() => setLoading(false));

  }, []);


  const findNodeByPath = (node, targetPath) => {
   

    if (!node) return null;
    if (node.path === targetPath) return node;
    if (node.children) {
      for (let child of node.children) {
        const result = findNodeByPath(child, targetPath);
        if (result) return result;
      }
    }
    
    return null;
  };



  useEffect(() => {
    loadReportTree();
  }, [loadReportTree]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileDrop = (event) => {
   
    event.preventDefault();
    setIsDragging(false);
    setLoading(true);
    const items = event.dataTransfer.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry && entry.isDirectory) {
          alert('Only files can be copied. Folders are not supported.');
          return;
        }
      }
    }

    const files = event.dataTransfer.files;

    // ✅ Use selectedFolderPath instead of selectedFiles[0]?.path
    const folderPath = selectedFolderPath?.replace(/^\/+/, '') || '';

    const formData = new FormData();
    for (let file of files) {
      formData.append('files', file);
    }
    formData.append('folderPath', folderPath);

    fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // optional, set default headers
      },
    })
      .then(() => {
        loadReportTree();
        // setTimeout(() => {
        //   if (lastSelectedNodeRef.current) {
        //     setSelectedFiles(lastSelectedNodeRef.current.files || []);
        //   }
        // }, 300);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };



  const handleFileInputChange = (e) => {
    setLoading(true); 
    const files = e.target.files;
    if (!files || files.length === 0) return;

    //const folderPath = selectedFiles[0]?.path?.split('/').slice(0, -1).join('/') || '';
    const folderPath = selectedFolderPath?.replace(/^\/+/, '') || '';

    const formData = new FormData();
    for (let file of files) {
      formData.append('files', file);
    }
    formData.append('folderPath', folderPath);

    fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // optional, set default headers
      },
    })
      .then(() => {

        loadReportTree();
        setTimeout(() => {
          reselectLastFolder(); // ⏪ simulate folder re-click after tree load
        }, 300); // slight delay to ensure tree is ready

        e.target.value = ''; // clear
      })

      .catch(console.error)
      .finally(() => setLoading(false));
  };


  const [folderContextMenu, setFolderContextMenu] = useState(null);

  const renderTree = (node) => {
    if (!node) return null;

    return (
      <TreeItem
        key={node.path}
        itemId={node.path}
        label={
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            onContextMenu={(e) => {
              e.preventDefault();
              setFolderContextMenu({
                mouseX: e.clientX + 2,
                mouseY: e.clientY - 4,
              });
              setContextFolderPath(node.path);
            }}
            sx={{
              cursor: 'context-menu',
              px: 1,
              py: 0.5,
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderRadius: 1,
              },
            }}
          >
            <Folder fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">{node.name}</Typography>
          </Box>
        }

        //         onClick={(event) => {
        //   event.stopPropagation();
        //   setSelectedFiles(node.files || []);
        //   setSelectedFilePath(null);
        //   setSelectedFolderPath(node.path); // ✅ this saves the clicked folder's relative path
        //  // lastSelectedNodeRef.current = node; // ✅ save the node
        //   lastSelectedPathRef.current = node.path;

        //   loadReportTree();
        // }}

        onClick={(event) => {
          event.stopPropagation();
          setSelectedFiles(node.files || []);
          setSelectedFilePath(null);
          setSelectedFolderPath(node.path);
          lastSelectedPathRef.current = node.path;
          loadReportTree();
        }}

        onContextMenu={(event) => {
          event.preventDefault();
          setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          });
          setContextFolderPath(node.path); // ✅ Set the right-clicked folder
        }}

      >
        {Array.isArray(node.children) && node.children.map(child => renderTree(child))}
      </TreeItem>
    );
  };



  useEffect(() => {
    if (reportTree) {
      reselectLastFolder();
    }
  }, [reportTree]);


  return (


    <Box p={2} position="relative">
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255,255,255,0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {/* <Box p={2}> */}
      <Grid container spacing={2}>
        {/* Folder Tree */}
        <Grid xs={12} md={4} lg={3}>
          <Typography variant="h6">Folders</Typography>
          <Divider sx={{ my: 1 }} />
          {reportTree ? (
            <SimpleTreeView>
              {renderTree(reportTree)}
            </SimpleTreeView>
          ) : (
            <Typography variant="body2">Loading folders...</Typography>
          )}
        </Grid>

        {/* File List and Upload Area */}


        <Grid xs={12} md={8} lg={9}>
          <Box
            p={2}
            border="2px dashed"
            borderColor={isDragging ? 'primary.main' : 'grey.400'}
            borderRadius={2}
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{ transition: 'border-color 0.3s ease-in-out', minHeight: 200 }}
          >

            <Button
              variant="contained"
              size="small"
              color="primary"
              fullWidth
              onClick={() => fileInputRef.current?.click()}
            >
              🖱️ Drag & Drop files ↔️ 📂Browse Files
            </Button>




            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (!files || files.length === 0) return;

                const folderPath = selectedFolderPath?.replace(/^\/+/, '') || ''; // ✅ sanitize

                const formData = new FormData();
                for (let file of files) {
                  formData.append('files', file);
                }
                formData.append('folderPath', folderPath); // ✅ pass selected folder

                fetch(`${API_BASE_URL}/api/upload`, {
                  method: 'POST',
                  body: formData,
                }, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`, // optional, set default headers
                  },
                })
                  .then((res) => res.json())
                  .then((data) => {
                    //  console.log('✅ Uploaded:', data);
                    loadReportTree();
                    // slight delay to ensure tree is ready     
                    e.target.value = ''; // clear file input
                  })
                  .catch(console.error);
              }}
            />








            {selectedFiles.length > 0 ? (





              <List dense>
                {selectedFiles.map((file) => (
                  <Tooltip
                    key={file.path}
                    title={`${file.name} Open/Download`}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          fontSize: '0.800rem',
                          border: '1px solid #ccc',
                        },
                      },
                      arrow: { sx: { color: '#333' } },
                    }}
                  >
                    <ListItem
                      button
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setFileContextMenu({
                          mouseX: e.clientX - 2,
                          mouseY: e.clientY - 4,
                        });
                        setContextFile(file);
                      }}
                    >
                      {getFileIcon(file.name)}
                      <ListItemText primary={file.name} />
                    </ListItem>
                  </Tooltip>
                ))}
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Typography variant="body2">
                    <strong>🎰 Total No of Files:</strong> {selectedFiles.length}
                  </Typography>
                </Box>


                {selectedFiles.length > 0 && selectedFolderPath && (
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        const url = `${API_BASE_URL}/api/download-folder?path=${encodeURIComponent(selectedFolderPath), {
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`, // optional, set default headers
                          },
                        }}`;
                        window.open(url, '_blank');
                      }}
                    >
                      🗃️ Download Full Folder as ZIP File
                    </Button>
                  </Box>
                )}


                <Menu
                  open={folderContextMenu !== null}
                  onClose={() => setFolderContextMenu(null)}
                  anchorReference="anchorPosition"
                  anchorPosition={
                    folderContextMenu !== null
                      ? { top: folderContextMenu.mouseY, left: folderContextMenu.mouseX }
                      : undefined
                  }
                >


                  <MenuItem
                    onClick={() => {
                      const folderName = prompt('Enter name for the new folder:');
                      if (!folderName || !contextFolderPath) {
                        setFolderContextMenu(null);
                        return;
                      }

                      fetch(`${API_BASE_URL}/api/create-folder`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          parentPath: contextFolderPath.replace(/^\/+/, '') || '',
                          newFolderName: folderName,
                        }),
                      })
                        .then((res) => res.json())
                        .then(() => {
                          loadReportTree();
                          lastSelectedNodeRef.current = { path: contextFolderPath };
                          setTimeout(() => reselectLastFolder(), 300);
                        })
                        .catch(console.error);

                      setFolderContextMenu(null);
                    }}
                  >
                    ➕ Create New Folder
                  </MenuItem>

                </Menu>






                <Menu
                  open={fileContextMenu !== null}
                  onClose={() => setFileContextMenu(null)}
                  anchorReference="anchorPosition"
                  anchorPosition={
                    fileContextMenu !== null
                      ? { top: fileContextMenu.mouseY, left: fileContextMenu.mouseX }
                      : undefined
                  }
                >
                  <MenuItem
                    disabled={!contextFile?.name?.endsWith('.pdf')}
                    onClick={() => {
                      window.open(`${API_BASE_URL}/reports${contextFile.path}`, '_blank', {
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`, // optional, set default headers
                        },
                      });
                      setFileContextMenu(null);
                    }}
                  >
                    📜 View
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      window.open(`${API_BASE_URL}/reports${contextFile.path}`, '_blank', {
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`, // optional, set default headers
                        },
                      });
                      setFileContextMenu(null);
                    }}
                  >
                    ⬇️ Download
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${contextFile.name}"?`)) {
                        fetch(`${API_BASE_URL}/api/deletefile`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                          },
                          body: JSON.stringify({ filePath: contextFile.path }),
                        })
                          .then(() => {
                            loadReportTree();
                            setSelectedFilePath(null);
                            setSelectedFiles((prev) =>
                              prev.filter((f) => f.path !== contextFile.path)
                            );
                          })
                          .catch(console.error);
                      }
                      setFileContextMenu(null);
                    }}
                  >
                    ❌ Delete
                  </MenuItem>


                  <MenuItem
                    onClick={() => {
                      const folderName = prompt('Enter name for the new folder:');
                      if (!folderName || !contextFolderPath) {
                        setFolderContextMenu(null);
                        return;
                      }

                      fetch(`${API_BASE_URL}/api/create-folder`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },

                        body: JSON.stringify({
                          parentPath: contextFolderPath.replace(/^\/+/, '') || '',
                          newFolderName: folderName,
                        }),
                      })
                        .then((res) => res.json())
                        .then(() => {
                          loadReportTree();
                          lastSelectedNodeRef.current = { path: contextFolderPath };
                          setTimeout(() => reselectLastFolder(), 300);
                        })
                        .catch(console.error);

                      setFolderContextMenu(null);
                    }}
                  >
                    ➕ Create New Folder on Root
                  </MenuItem>
                </Menu>

              </List>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Select a folder to view files.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FolderViewer;
